
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BabyProfile {
  name: string;
  birthday?: string; // ISO date string
  originalHeight?: string;
  originalWeight?: string;
  gender?: 'male' | 'female' | 'other';
}

// Save profile to localStorage and Supabase if user is logged in
export const saveBabyProfile = async (profile: BabyProfile): Promise<void> => {
  // Always save to localStorage for immediate access
  localStorage.setItem('babyProfile', JSON.stringify(profile));
  
  // If user is logged in, save to Supabase
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    try {
      // Check if profile already exists
      const { data: existingProfile } = await supabase
        .from('baby_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (existingProfile) {
        // Update existing profile
        await supabase
          .from('baby_profiles')
          .update({
            name: profile.name,
            birthday: profile.birthday || null,
            gender: profile.gender,
            original_height: profile.originalHeight,
            original_weight: profile.originalWeight,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingProfile.id);
      } else {
        // Create new profile
        await supabase
          .from('baby_profiles')
          .insert({
            user_id: user.id,
            name: profile.name,
            birthday: profile.birthday || null,
            gender: profile.gender,
            original_height: profile.originalHeight,
            original_weight: profile.originalWeight
          });
      }
    } catch (error) {
      console.error('Error saving baby profile to Supabase:', error);
      // Still show success since we saved to localStorage
    }
  }
};

// Fetch profile from Supabase if user is logged in, otherwise from localStorage
export const getBabyProfile = async (): Promise<BabyProfile | null> => {
  try {
    // Check if user is logged in
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      // Try to get profile from Supabase first
      const { data: babyProfile, error } = await supabase
        .from('baby_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (babyProfile && !error) {
        // Convert from Supabase format to app format
        const formattedProfile: BabyProfile = {
          name: babyProfile.name,
          birthday: babyProfile.birthday,
          gender: babyProfile.gender as 'male' | 'female' | 'other',
          originalHeight: babyProfile.original_height,
          originalWeight: babyProfile.original_weight
        };
        
        // Update localStorage with the latest data
        localStorage.setItem('babyProfile', JSON.stringify(formattedProfile));
        
        return formattedProfile;
      }
    }
    
    // Fallback to localStorage if no user or no Supabase data
    const profile = localStorage.getItem('babyProfile');
    return profile ? JSON.parse(profile) : null;
  } catch (error) {
    console.error('Error fetching baby profile:', error);
    // Fallback to localStorage on error
    const profile = localStorage.getItem('babyProfile');
    return profile ? JSON.parse(profile) : null;
  }
};

// Synchronous version for components that can't use async/await
export const getBabyProfileSync = (): BabyProfile | null => {
  const profile = localStorage.getItem('babyProfile');
  return profile ? JSON.parse(profile) : null;
};

// Check if a profile exists (in localStorage)
export const hasBabyProfile = (): boolean => {
  return localStorage.getItem('babyProfile') !== null;
};

// Update part of a profile
export const updateBabyProfile = async (updates: Partial<BabyProfile>): Promise<void> => {
  const currentProfile = await getBabyProfile() || { name: '' };
  await saveBabyProfile({ ...currentProfile, ...updates });
};

// Sync local baby profile with Supabase
export const syncBabyProfile = async (): Promise<void> => {
  try {
    // Check if user is logged in
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    // Check if profile exists in localStorage
    const localProfile = localStorage.getItem('babyProfile');
    if (!localProfile) return;
    
    const profile = JSON.parse(localProfile) as BabyProfile;
    
    // Check if profile exists in Supabase
    const { data: existingProfile } = await supabase
      .from('baby_profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    
    if (existingProfile) {
      // Compare timestamps to determine which is newer
      const remoteUpdatedAt = new Date(existingProfile.updated_at);
      const localStorageLastModified = localStorage.getItem('babyProfileLastModified');
      const localUpdatedAt = localStorageLastModified ? new Date(localStorageLastModified) : new Date(0);
      
      if (remoteUpdatedAt > localUpdatedAt) {
        // Remote is newer, update localStorage
        const formattedProfile: BabyProfile = {
          name: existingProfile.name,
          birthday: existingProfile.birthday,
          gender: existingProfile.gender as 'male' | 'female' | 'other',
          originalHeight: existingProfile.original_height,
          originalWeight: existingProfile.original_weight
        };
        
        localStorage.setItem('babyProfile', JSON.stringify(formattedProfile));
        localStorage.setItem('babyProfileLastModified', remoteUpdatedAt.toISOString());
      } else {
        // Local is newer or same, update Supabase
        await supabase
          .from('baby_profiles')
          .update({
            name: profile.name,
            birthday: profile.birthday || null,
            gender: profile.gender,
            original_height: profile.originalHeight,
            original_weight: profile.originalWeight,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingProfile.id);
          
        localStorage.setItem('babyProfileLastModified', new Date().toISOString());
      }
    } else {
      // No profile in Supabase, create new one
      await supabase
        .from('baby_profiles')
        .insert({
          user_id: user.id,
          name: profile.name,
          birthday: profile.birthday || null,
          gender: profile.gender,
          original_height: profile.originalHeight,
          original_weight: profile.originalWeight
        });
        
      localStorage.setItem('babyProfileLastModified', new Date().toISOString());
    }
  } catch (error) {
    console.error('Error syncing baby profile:', error);
  }
};
